import { getReadableLabelColor } from "@/lib/color";
import { cn } from "@/lib/utils";
import { Label } from "@repo/schema";
import { ArrowLeft, ArrowRight, Pencil } from "lucide-react";
import { CSSProperties, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { GenerateLabelDescription } from "../GenerateDescriptionButton";
import { ArchivedLabelsPopover } from "./ArchivedLabelsPopover";
import { LabelEditorInput, LabelEditorPopover } from "./LabelEditorPopover";

export interface ClassSelectProps {
  labels: Label[];
  activeLabelId: number | null;
  projectId: number | undefined;
  /** The open image has unsaved edits; archiving a label waits for the save. */
  hasUnsavedChanges: boolean;
  onSelectLabel: (labelId: number) => void;
  onCreateLabel: (input: LabelEditorInput) => Promise<void>;
  onUpdateLabel: (labelId: number, input: LabelEditorInput) => Promise<void>;
  onArchiveLabel: (labelId: number) => Promise<void>;
  onRestoreLabel: (labelId: number) => Promise<void>;
  /** Absent when Robopipe Vision is unavailable. See LabelEditorPopover. */
  onGenerateDescription?: GenerateLabelDescription;
  isLoadingLabels?: boolean;
}

/** Digit shown on a chip: the hotkeys are positional, `1`-`9` then `0`. */
const shortcutFor = (index: number): string | null =>
  index < 9 ? String(index + 1) : index === 9 ? "0" : null;

/**
 * Paints one label's shortcut digit as a physical keycap. The digit sits on a
 * tint of the label color rather than the color itself, so the ink is darkened
 * until it stays legible on that tint (see `getReadableLabelColor`) and the
 * rings are drawn from the same ink. `0 1.5px 0` is the cap's side wall: on
 * hover it shrinks to `0 0.5px 0` while the cap translates down 1px, so the
 * bottom edge stays put and the key reads as pressed.
 */
const keycapVars = (color: string): CSSProperties => {
  const face = (pct: number) =>
    `color-mix(in srgb, ${color} ${pct}%, transparent)`;
  const ink = getReadableLabelColor(color);
  const edge = (pct: number) =>
    `color-mix(in srgb, ${ink} ${pct}%, transparent)`;
  return {
    "--key-face": face(16),
    "--key-face-pressed": face(24),
    "--key-ink": ink,
    "--key-cap": `inset 0 0 0 1px ${edge(30)}, 0 1.5px 0 ${edge(22)}`,
    "--key-cap-pressed": `inset 0 0 0 1px ${edge(45)}, 0 0.5px 0 ${edge(30)}`,
  } as CSSProperties;
};

const arrowClass =
  "flex size-[34px] shrink-0 cursor-pointer items-center justify-center rounded-lg text-foreground/60 transition-colors outline-none hover:bg-black/5 focus-visible:ring-2 focus-visible:ring-ring/50";

/**
 * Overlay holding one scroll arrow. It floats over the edge of the chip row
 * (so showing it never reflows the chips) on a short white gradient that keeps
 * the arrow legible over whatever chip is underneath, and fades rather than
 * mounts so the transition is smooth.
 */
const ArrowOverlay = ({
  side,
  visible,
  onClick,
  label,
}: {
  side: "left" | "right";
  visible: boolean;
  onClick: () => void;
  label: string;
}) => (
  <div
    className={cn(
      "pointer-events-none absolute inset-y-0 flex items-center transition-opacity duration-150",
      side === "left"
        ? "left-0 bg-gradient-to-r from-white via-white/90 to-transparent pr-6"
        : "right-0 bg-gradient-to-l from-white via-white/90 to-transparent pl-6",
      visible ? "opacity-100" : "opacity-0",
    )}
  >
    <button
      type="button"
      aria-label={label}
      aria-hidden={!visible}
      tabIndex={visible ? 0 : -1}
      onClick={onClick}
      className={cn(arrowClass, visible && "pointer-events-auto")}
    >
      {side === "left" ? (
        <ArrowLeft className="size-5" />
      ) : (
        <ArrowRight className="size-5" />
      )}
    </button>
  </div>
);

/**
 * Floating class toolbar under the canvas: one chip per project label with
 * its hotkey digit, the `+` / pencil label editor, and at the end the archived
 * labels list.
 */
export const ClassSelect = ({
  labels,
  activeLabelId,
  projectId,
  hasUnsavedChanges,
  onSelectLabel,
  onCreateLabel,
  onUpdateLabel,
  onArchiveLabel,
  onRestoreLabel,
  onGenerateDescription,
  isLoadingLabels,
}: ClassSelectProps) => {
  const { t } = useTranslation(["label", "common"]);
  const barRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingLabel, setEditingLabel] = useState<Label | null>(null);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const update = () => {
      setCanScrollLeft(el.scrollLeft > 1);
      setCanScrollRight(
        el.scrollLeft + el.clientWidth < el.scrollWidth - 1,
      );
    };
    update();
    el.addEventListener("scroll", update, { passive: true });
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => {
      el.removeEventListener("scroll", update);
      ro.disconnect();
    };
  }, [labels]);

  // Keep the active label in view when it changes (e.g. arrow-key cycling).
  useEffect(() => {
    if (activeLabelId === null) return;
    const container = scrollRef.current;
    if (!container) return;
    const active = container.querySelector<HTMLElement>(
      `[data-label-id="${activeLabelId}"]`,
    );
    if (!active) return;
    active.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
      inline: "nearest",
    });
  }, [activeLabelId]);

  const scrollByAmount = (delta: number) => {
    scrollRef.current?.scrollBy({ left: delta, behavior: "smooth" });
  };

  // The `+` trigger (and outside clicks / Escape) go through here; a pencil
  // click sets the label first and opens directly, so `true` here always
  // means "create".
  const handleEditorOpenChange = (next: boolean) => {
    if (next) setEditingLabel(null);
    setEditorOpen(next);
  };

  const openEditor = (label: Label) => {
    setEditingLabel(label);
    setEditorOpen(true);
  };

  const isEmpty = labels.length === 0 && !isLoadingLabels;

  return (
    <div
      ref={barRef}
      className="flex max-w-full items-center gap-2 overflow-hidden rounded-[18px] bg-white/[0.96] p-2.5 shadow-[0_4px_16px_rgba(0,0,0,0.12),inset_0_0_0_1px_rgba(0,0,0,0.08)]"
    >
      <LabelEditorPopover
        open={editorOpen}
        onOpenChange={handleEditorOpenChange}
        anchor={barRef}
        editing={editingLabel}
        existingLabels={labels}
        projectId={projectId}
        hasUnsavedChanges={hasUnsavedChanges}
        onCreate={onCreateLabel}
        onUpdate={onUpdateLabel}
        onArchive={onArchiveLabel}
        onRestore={onRestoreLabel}
        onGenerateDescription={onGenerateDescription}
      />

      {isEmpty ? (
        <span className="whitespace-nowrap px-2 text-[13px] text-muted-foreground">
          {t("classSelect.empty")}
        </span>
      ) : (
        <div className="relative min-w-0 flex-1">
          {/* scroll-px keeps scrollIntoView from parking the active chip under an arrow. */}
          <div
            ref={scrollRef}
            className="flex items-center gap-2 overflow-x-auto scroll-px-10 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
          >
            {labels.map((label, index) => {
              const isActive = activeLabelId === label.id;
              const shortcut = shortcutFor(index);
              return (
                // Select and edit are sibling buttons: a button may not contain
                // another one, and the pencil must not fire the select.
                <div
                  key={label.id}
                  className={cn(
                    "group flex shrink-0 items-center rounded-lg transition-colors",
                    isActive
                      ? "shadow-[inset_0_0_0_1px_rgba(0,0,0,0.08)]"
                      : "bg-black/[0.03] hover:bg-black/5",
                  )}
                  style={
                    isActive
                      ? {
                          background: `color-mix(in srgb, ${label.color} 18%, transparent)`,
                        }
                      : undefined
                  }
                >
                  <button
                    type="button"
                    data-label-id={label.id}
                    onClick={() => onSelectLabel(label.id)}
                    title={shortcut ? `${label.name} (${shortcut})` : label.name}
                    className="flex cursor-pointer items-center gap-2.5 rounded-lg py-1.5 pl-1.5 outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
                  >
                    {shortcut ? (
                      <span
                        // `translate` (not `transform`) is what Tailwind's
                        // translate utilities write, so that is what has to be
                        // transitioned for the press to ease rather than snap.
                        className="flex h-5 min-w-5 shrink-0 items-center justify-center rounded-[5px] bg-(--key-face) px-1 text-[11px] leading-none font-bold text-(--key-ink) tabular-nums transition-[translate,box-shadow,background-color] duration-[120ms] ease-[ease] [box-shadow:var(--key-cap)] group-hover:translate-y-px group-hover:bg-(--key-face-pressed) group-hover:[box-shadow:var(--key-cap-pressed)]"
                        style={keycapVars(label.color)}
                      >
                        {shortcut}
                      </span>
                    ) : (
                      // Past the tenth label there is no hotkey left to show,
                      // so the chip falls back to a plain color swatch.
                      <span className="flex size-5 shrink-0 items-center justify-center">
                        <span
                          className="block size-2.5 rounded-[3px] shadow-[inset_0_0_0_1px_rgba(0,0,0,0.12)]"
                          style={{ background: label.color }}
                        />
                      </span>
                    )}
                    <span className="whitespace-nowrap text-[13px] leading-[18px] text-foreground/90">
                      {label.name}
                    </span>
                  </button>
                  <button
                    type="button"
                    aria-label={t("classSelect.editLabelAria", { name: label.name })}
                    title={t("classSelect.editLabel")}
                    onClick={() => openEditor(label)}
                    className="ml-2.5 mr-2 flex size-[18px] shrink-0 cursor-pointer items-center justify-center rounded text-foreground/60 opacity-0 transition-opacity outline-none group-hover:opacity-100 group-focus-within:opacity-100 hover:text-foreground focus-visible:opacity-100 focus-visible:ring-2 focus-visible:ring-ring/50"
                  >
                    <Pencil className="size-3.5" />
                  </button>
                </div>
              );
            })}
          </div>
          <ArrowOverlay
            side="left"
            visible={canScrollLeft}
            onClick={() => scrollByAmount(-240)}
            label={t("classSelect.scrollLeft")}
          />
          <ArrowOverlay
            side="right"
            visible={canScrollRight}
            onClick={() => scrollByAmount(240)}
            label={t("classSelect.scrollRight")}
          />
        </div>
      )}

      {/* Last in the bar, behind every active label; absent until one is archived. */}
      <ArchivedLabelsPopover
        projectId={projectId}
        anchor={barRef}
        hasUnsavedChanges={hasUnsavedChanges}
        onRestore={onRestoreLabel}
      />
    </div>
  );
};
